'use client';

import { Global } from '@emotion/react';
import type { Theme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createContext, type ReactNode, useMemo, useRef } from 'react';

import type { RawProjectSettingsApiResponse } from '@campaign-manager/core/model';
import { buildTheme, rmtPrimaryColor, rmtSecondaryColor } from '@campaign-manager/theme';
import globalStyles from '@campaign-manager/theme/globalStyles';

const ThemeContext = createContext<undefined>(undefined);

export function ThemeProvider({
  children,
  projectSettings,
}: {
  children: ReactNode;
  projectSettings: RawProjectSettingsApiResponse | null | undefined;
}) {
  const projectSettingsRef = useRef(JSON.stringify(projectSettings));
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const theme = useMemo<Theme>(() => {
    if (
      (projectSettings?.primary_color && !isValidHexColor(projectSettings?.primary_color)) ||
      (projectSettings?.secondary_color && !isValidHexColor(projectSettings?.secondary_color))
    ) {
      console.warn(
        `Invalid project settings (${projectSettings?.project_key}). Colors must be valid hex color strings.`,
        'Primary color:',
        projectSettings?.primary_color,
        'secondary color:',
        projectSettings?.secondary_color,
      );
    }

    const { primary_color, secondary_color } = projectSettings || {};
    const primary = primary_color || rmtPrimaryColor;
    const secondary = secondary_color || rmtSecondaryColor;

    return buildTheme(primary, secondary);
  }, [projectSettingsRef]);

  return (
    <ThemeContext.Provider value={undefined}>
      <MuiThemeProvider theme={theme}>
        <Global styles={globalStyles} />
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

function isValidHexColor(color: string) {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color);
}
